import { SidebarTooltip } from '../SidebarTooltip/SidebarTooltip.js';
import { useCurrentUser } from '@/hooks/useCurrentUser.js';
import { styled } from '@/stitches/index.js';

type Variant = 'normal' | 'small';

type ColorScheme = 'gray' | 'normal' | 'red';

const Badge = styled('span', {
  alignContent: 'center',
  alignItems: 'center',
  borderRadius: '$4',
  display: 'inline-flex',
  fontFamily: '$inter',
  fontWeight: '$semiBold',
  gap: '$12',
  justifyContent: 'space-between',
  letterSpacing: '$0_5',
  textTransform: 'uppercase',
  variants: {
    colorScheme: {
      gray: {
        background: '$gray10',
        color: '$gray70',
      },
      normal: {
        background: `linear-gradient(90deg, rgba(243, 202, 77, 0.50) 0%, rgba(255, 152, 154, 0.50) 45.31%, rgba(203, 171, 255, 0.50) 100%), #FFF`,
        color: '$yellow80',
      },
      red: {
        background: '$brandWatermelon',
        color: '$white',
      },
    },
    variant: {
      normal: {
        fontSize: '$12',
        height: '$24',
        padding: '$2 $8',
      },
      small: {
        bottom: '$1',
        fontSize: '$10',
        padding: '$3 $4',
        position: 'relative',
      },
    },
  },
});

type Props = {
  // Force rendering for both Pro and non-Pro users
  readonly alwaysVisible?: boolean;
  readonly colorScheme?: ColorScheme;
  readonly label?: string;
  readonly variant?: Variant;
};

export const ContraProBadge = ({
  alwaysVisible = false,
  colorScheme = 'normal',
  label = 'Pro',
  variant = 'normal',
}: Props) => {
  const currentUser = useCurrentUser();
  const hasActiveSubscription =
    currentUser?.userAccount?.profile.hasActivePortfolioSubscription;

  const tooltipContent = hasActiveSubscription
    ? 'Only Pro members have access to customizations.'
    : 'Unlock customizations by going Pro.';

  if (hasActiveSubscription && !alwaysVisible) return null;

  return (
    <SidebarTooltip
      ariaLabel={tooltipContent}
      content={tooltipContent}
      side="left"
      sideOffset={4}
    >
      <Badge
        colorScheme={colorScheme}
        variant={variant}
      >
        <span>{label}</span>
      </Badge>
    </SidebarTooltip>
  );
};
